import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { OrganizationDetail } from '@/features/super-admin/components/organizations/OrganizationDetail'
import { EngagementMetrics } from '@/features/super-admin/components/organizations/EngagementMetrics'
import { EngagementTrend } from '@/features/super-admin/components/organizations/EngagementTrend'
import { MembersTab } from '@/features/super-admin/components/organizations/MembersTab'
import { AlertModal } from '@/features/super-admin/components/AlertModal'
import { useOrganization } from '@/features/super-admin/hooks/use-organization'
import {
  useDeleteOrganization,
  useSuspendOrganization,
  useReactivateOrganization,
  useUpdateOrganization,
} from '@/features/super-admin/hooks/use-organizations'

export const Route = createFileRoute('/super-admin/organizations/$orgId')({
  component: OrganizationDetailPage,
})

function OrganizationDetailPage() {
  const navigate = useNavigate()
  const { onMenuToggle } = useMenuToggle()
  const { orgId } = Route.useParams()
  const { data: organization, isLoading } = useOrganization(orgId)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const [errorModal, setErrorModal] = useState<{ title: string; message: string } | null>(null)

  const suspendMutation = useSuspendOrganization()
  const reactivateMutation = useReactivateOrganization()
  const updateMutation = useUpdateOrganization()
  const deleteMutation = useDeleteOrganization()

  const handleBack = () => {
    navigate({ to: '/super-admin/organizations' })
  }

  const handleSuspend = async () => {
    const reason = prompt('Informe o motivo da suspensão (mínimo 10 caracteres):')
    if (reason && reason.length >= 10) {
      try {
        await suspendMutation.mutateAsync({ id: orgId, data: { reason, notify_admins: true } })
      } catch (error) {
        console.error('Failed to suspend organization:', error)
      }
    }
  }

  const handleReactivate = async () => {
    try {
      await reactivateMutation.mutateAsync({
        id: orgId,
        data: { notify_admins: true },
      })
    } catch (error) {
      console.error('Failed to reactivate organization:', error)
    }
  }

  const handleEdit = async () => {
    // For now, just show a basic prompt for editing name
    if (!organization) return

    const newName = prompt('Novo nome da organização:', organization.name)
    if (newName && newName !== organization.name) {
      try {
        await updateMutation.mutateAsync({ id: orgId, data: { name: newName } })
      } catch (error) {
        console.error('Failed to update organization:', error)
      }
    }
  }

  const handleDelete = async () => {
    if (!organization || deleteConfirmName !== organization.name) return

    try {
      await deleteMutation.mutateAsync({
        id: orgId,
        data: {
          confirm_name: deleteConfirmName,
          reason: 'Workspace de teste removido pelo Super Admin',
        },
      })
      setDeleteModalOpen(false)
      navigate({ to: '/super-admin/organizations' })
    } catch (error) {
      setErrorModal({
        title: 'Não foi possível excluir',
        message: error instanceof Error ? error.message : 'Erro desconhecido ao excluir organização.',
      })
    }
  }

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Detalhes da Organização"
        subtitle={organization?.name || 'Carregando...'}
        onMenuToggle={onMenuToggle}
      />

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-5 space-y-5">
        {organization && (
          <>
            <OrganizationDetail
              organization={organization}
              onBack={handleBack}
              onSuspend={handleSuspend}
              onReactivate={handleReactivate}
              onEdit={handleEdit}
              onDelete={() => {
                setDeleteConfirmName('')
                setDeleteModalOpen(true)
              }}
              loading={isLoading}
            />

            {/* Members Section */}
            <MembersTab organizationId={orgId} />

            {/* Engagement Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <EngagementMetrics
                data={{
                  engagement_score: organization.metrics?.engagement_score ?? 0,
                  active_members: organization.metrics?.active_members ?? 0,
                  active_patients: organization.metrics?.active_patients ?? 0,
                  reports_30d: organization.metrics?.reports_30d ?? 0,
                  messages_30d: organization.metrics?.messages_30d ?? 0,
                  last_activity_at: organization.last_activity_at ?? null,
                }}
                loading={isLoading}
              />
              <EngagementTrend
                data={[]}
                loading={isLoading}
              />
            </div>
          </>
        )}
        {isLoading && !organization && (
          <div className="space-y-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48" />
            <div className="h-32 bg-gray-100 rounded-xl" />
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-100 rounded-xl" />
              ))}
            </div>
          </div>
        )}
      </div>

      {organization && deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteModalOpen(false)} />
          <div className="relative mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 p-4">
              <h3 className="text-base font-semibold text-gray-900">Excluir workspace de teste</h3>
              <p className="mt-1 text-sm text-gray-500">
                A exclusão é permanente e só será aceita para workspaces sem dados clínicos ou financeiros.
              </p>
            </div>
            <div className="space-y-3 p-4">
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                Digite <strong>{organization.name}</strong> para confirmar.
              </div>
              <input
                value={deleteConfirmName}
                onChange={(event) => setDeleteConfirmName(event.target.value)}
                className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-red-300 focus:ring-2 focus:ring-red-100"
                placeholder={organization.name}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 p-4">
              <button
                type="button"
                onClick={() => setDeleteModalOpen(false)}
                className="h-9 rounded-lg px-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
                disabled={deleteMutation.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteConfirmName !== organization.name || deleteMutation.isPending}
                className="h-9 rounded-lg bg-red-600 px-3 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {errorModal && (
        <AlertModal
          isOpen
          title={errorModal.title}
          message={errorModal.message}
          variant="error"
          onClose={() => setErrorModal(null)}
        />
      )}
    </div>
  )
}
